import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getGiftAids,
  getGiftAid,
  getGiftAidsWithAddress,
  getGiftAidAggregateSum,
  getGiftAidApplications,
  getGiftAidApplicationAggregates,
  getGiftAidsByApplicationId,
  getGiftAidAggregateByApplicationId,
  createGiftAidApplication,
  updateGiftAidsStatus,
  updateGiftAidApplicationStatus,
  updateGiftAidStatus
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';

// Hook to fetch all gift aids
export function useGiftAids(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAids', organization?.id, branchId, filters],
    queryFn: () => getGiftAids(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    giftAids: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch gift aids with address (for unclaimed transactions)
export function useGiftAidsWithAddress(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAidsWithAddress', organization?.id, branchId, filters],
    queryFn: () => getGiftAidsWithAddress(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    giftAids: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch gift aid aggregate sum
export function useGiftAidAggregateSum(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAidAggregateSum', organization?.id, branchId, filters],
    queryFn: () => getGiftAidAggregateSum(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    aggregateSum: data?.data?.sum || 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single gift aid
export function useGiftAid(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAid', id],
    queryFn: () => getGiftAid(id),
    enabled: !!id
  });

  return {
    giftAid: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch all gift aid applications
export function useGiftAidApplications() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAidApplications', organization?.id, branchId],
    queryFn: () => getGiftAidApplications(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    giftAidApplications: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch gift aid application aggregates
export function useGiftAidApplicationAggregates() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAidApplicationAggregates', organization?.id, branchId],
    queryFn: () => getGiftAidApplicationAggregates(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    aggregates: data?.data || {
      completedCount: 0,
      submittedCount: 0,
      awaitingCount: 0
    },
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch gift aids by application ID
export function useGiftAidsByApplicationId(applicationId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAidsByApplicationId', applicationId],
    queryFn: () => getGiftAidsByApplicationId(applicationId),
    enabled: !!applicationId
  });

  return {
    giftAids: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch gift aid aggregate by application ID
export function useGiftAidAggregateByApplicationId(applicationId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giftAidAggregateByApplicationId', applicationId],
    queryFn: () => getGiftAidAggregateByApplicationId(applicationId),
    enabled: !!applicationId
  });

  return {
    aggregate: data?.data || {
      sum: 0,
      count: 0,
      acceptedCount: 0,
      rejectedCount: 0,
      nodes: []
    },
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create gift aid application
export function useCreateGiftAidApplication() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();

  return useMutation({
    mutationFn: applicationData =>
      createGiftAidApplication({
        ...applicationData,
        branch_id: branchId
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([
        'giftAidApplications',
        organization?.id,
        branchId
      ]);
      queryClient.invalidateQueries([
        'giftAidsWithAddress',
        organization?.id,
        branchId
      ]);
      queryClient.invalidateQueries([
        'giftAidAggregateSum',
        organization?.id,
        branchId
      ]);
    }
  });
}

// Hook to update gift aids status
export function useUpdateGiftAidsStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ giftAidIds, status, applicationId }) =>
      updateGiftAidsStatus(giftAidIds, status, applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['giftAids']);
      queryClient.invalidateQueries(['giftAidApplications']);
    }
  });
}

// Hook to update gift aid application status
export function useUpdateGiftAidApplicationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, submittedDate }) =>
      updateGiftAidApplicationStatus(id, status, submittedDate),
    onSuccess: () => {
      queryClient.invalidateQueries(['giftAidApplications']);
      queryClient.invalidateQueries(['giftAids']);
    }
  });
}

// Hook to update gift aid status
export function useUpdateGiftAidStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }) => updateGiftAidStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['giftAids']);
      queryClient.invalidateQueries(['giftAidsByApplicationId']);
      queryClient.invalidateQueries(['giftAidAggregateByApplicationId']);
    }
  });
}
